// HITL decision page — the interview view for one paused run, reached
// from a HITL row in the Inbox (or a deep link; the legacy
// `/triage/hitl/:runId` URL redirects here). This is the console's only
// surface that resolves a HITL pause (POST /v1/runs/:id/resume via
// HitlResolveForm).
import { useClient } from '@cortex/react-query';
import type { HitlContext } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { BackButton } from '../../../components/common/BackButton';
import { EmptyState } from '../../../components/common/EmptyState';
import { PageHeader } from '../../../components/common/PageHeader';
import { Spinner } from '../../../components/common/Spinner';
import { HitlResolveForm } from '../../../components/hitl/HitlResolveForm';
import { routes } from '../../../lib/constants';
import { formatRelative } from '../../../lib/format';

export const Route = createFileRoute('/inbox/hitl/$runId')({ component: HitlDetailPage });

const sectionTitleStyle = {
  fontSize: 'var(--fs-xs)',
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  color: 'var(--text-secondary)',
  marginBottom: 'var(--sp-2)',
};

const codeBlockStyle = {
  background: 'var(--surface-2)',
  padding: 'var(--sp-3)',
  borderRadius: 'var(--radius-sm)',
  overflow: 'auto',
  fontSize: 12,
  lineHeight: 1.5,
};

function HitlDetailPage() {
  const { runId } = Route.useParams();
  const client = useClient();
  const nav = useNavigate();

  const ctx = useQuery({
    queryKey: ['hitl', runId],
    queryFn: () => client.http.get(`v1/hitl/${runId}`).json<HitlContext>(),
  });

  if (ctx.isLoading) return <Spinner />;
  if (ctx.error)
    return (
      <div className="alert alert--danger">Failed to load: {(ctx.error as Error).message}</div>
    );
  if (!ctx.data)
    return (
      <EmptyState
        title="Not found"
        description="This run is not paused or no HITL request is pending."
      />
    );

  const c = ctx.data;
  const hasContext = Object.keys(c.context).length > 0;

  return (
    <>
      <PageHeader
        title={c.workflowName}
        actions={<BackButton to={routes.INBOX} label="Back to Inbox" />}
      />
      <div className="card">
        <div
          className="card-body"
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}
        >
          {c.question ? (
            <div>
              <p style={sectionTitleStyle}>Question</p>
              <p>{c.question}</p>
            </div>
          ) : null}

          <div>
            <p style={sectionTitleStyle}>Decision</p>
            <HitlResolveForm
              context={c}
              onResolved={async () => {
                try {
                  await nav({ to: routes.INBOX });
                } catch (err) {
                  console.error(err);
                }
              }}
            />
          </div>

          {hasContext ? (
            <div>
              <p style={sectionTitleStyle}>Context provided at pause</p>
              <pre className="t-mono" style={codeBlockStyle}>
                {JSON.stringify(c.context, null, 2)}
              </pre>
            </div>
          ) : null}

          <div>
            <p style={sectionTitleStyle}>Run input</p>
            <pre className="t-mono" style={codeBlockStyle}>
              {JSON.stringify(c.input, null, 2)}
            </pre>
          </div>

          <div>
            <p style={sectionTitleStyle}>Metadata</p>
            <p className="t-xs t-muted">
              Run <code className="t-mono">{c.runId}</code> · paused {formatRelative(c.pausedAt)}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
